import { useAuthStore } from '@stores/authStore';
import { usersMutations } from '@therame/api-client';
import { Role, UpdateUserSettingsDto, UserSettingsFullSizeDto } from '@therame/shared';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

type UserSettingsState = {
  settings: Ref<UserSettingsFullSizeDto | undefined>;
  loadSettings: (newSettings: UserSettingsFullSizeDto) => void;
  setSetting: <K extends keyof UserSettingsFullSizeDto>(
    key: K,
    value: UserSettingsFullSizeDto[K],
  ) => void;
  updateSettings: () => Promise<UserSettingsFullSizeDto>;
};

export const useUserSettingsStore = defineStore('userSettings', (): UserSettingsState => {
  const authStore = useAuthStore();
  const settings = ref<UserSettingsFullSizeDto | undefined>(undefined);

  const loadSettings = (newSettings: UserSettingsFullSizeDto) => {
    if (authStore.authContext?.user?.role !== Role.SUPERADMIN) {
      settings.value = newSettings;
    }
  };

  const setSetting = <K extends keyof UserSettingsFullSizeDto>(
    key: K,
    value: UserSettingsFullSizeDto[K],
  ) => {
    if (settings.value) {
      settings.value = { ...settings.value, [key]: value };
    }
  };

  const updateSettings = async (): Promise<UserSettingsFullSizeDto> => {
    const userId = authStore.authContext?.user?.id;
    const s = settings.value;
    if (!userId || !s) {
      throw new Error('No user or settings available');
    }
    const updateDto: UpdateUserSettingsDto = {
      systemNotificationRegistrationEnabled: s.systemNotificationRegistrationEnabled,
      systemNotificationShopEnabled: s.systemNotificationShopEnabled,
      systemNotificationPatientHandOverEnabled: s.systemNotificationPatientHandOverEnabled,
      systemNotificationAppointmentEnabled: s.systemNotificationAppointmentEnabled,
      systemNotificationExerciseProgramEnabled: s.systemNotificationExerciseProgramEnabled,
      systemNotificationTrainingReminderEnabled: s.systemNotificationTrainingReminderEnabled,
      systemNotificationProgramStatsEnabled: s.systemNotificationProgramStatsEnabled,
      systemNotificationInvoicesEnabled: s.systemNotificationInvoicesEnabled,
      emailNotificationRegistrationEnabled: s.emailNotificationRegistrationEnabled,
      emailNotificationShopEnabled: s.emailNotificationShopEnabled,
      emailNotificationPatientHandOverEnabled: s.emailNotificationPatientHandOverEnabled,
      emailNotificationAppointmentEnabled: s.emailNotificationAppointmentEnabled,
      emailNotificationExerciseProgramEnabled: s.emailNotificationExerciseProgramEnabled,
      emailNotificationTrainingReminderEnabled: s.emailNotificationTrainingReminderEnabled,
      emailNotificationProgramStatsEnabled: s.emailNotificationProgramStatsEnabled,
      emailNotificationInvoicesEnabled: s.emailNotificationInvoicesEnabled,
      pushNotificationRegistrationEnabled: s.pushNotificationRegistrationEnabled,
      pushNotificationShopEnabled: s.pushNotificationShopEnabled,
      pushNotificationPatientHandOverEnabled: s.pushNotificationPatientHandOverEnabled,
      pushNotificationAppointmentEnabled: s.pushNotificationAppointmentEnabled,
      pushNotificationExerciseProgramEnabled: s.pushNotificationExerciseProgramEnabled,
      pushNotificationTrainingReminderEnabled: s.pushNotificationTrainingReminderEnabled,
      pushNotificationProgramStatsEnabled: s.pushNotificationProgramStatsEnabled,
      pushNotificationInvoicesEnabled: s.pushNotificationInvoicesEnabled,
      trainingReminderTime: s.trainingReminderTime,
      defaultVideoCallDuration: s.defaultVideoCallDuration,
    };
    const updated = await usersMutations.updateSettings(userId, updateDto);
    settings.value = updated;
    return updated;
  };

  const store: UserSettingsState = {
    settings,
    loadSettings,
    setSetting,
    updateSettings,
  };
  return store;
});
